<template>
  <article class="invoice-create-card invoice-create-card--accent-contacts payment-receipt-payment-info-card">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>
        <i class="fa fa-credit-card invoice-create-section-icon invoice-create-section-icon--contacts" aria-hidden="true"></i>
        {{ text?.create?.ui?.paymentInfoCard?.title || 'Información del Pago' }}
      </h3>

      <div class="payment-receipt-payment-info-card__head-actions">
        <button
          v-if="showBankFields"
          type="button"
          class="payment-receipt-payment-info-card__banks-toggle"
          @click="updateBankField('expanded', !bankDetails.expanded)"
        >
          <span>{{ paymentsText?.payModal?.banking?.fields?.banks || 'Bancos' }}</span>
          <i class="fa" :class="bankDetails.expanded ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
        </button>

        <div class="payment-receipt-payment-info-card__folio-box">
          <strong>{{ text?.create?.ui?.paymentInfoCard?.fields?.folioPrefix || 'Folio' }}: {{ paymentFolio }}</strong>
        </div>
      </div>
    </header>

    <div class="invoice-create-info-grid payment-receipt-payment-info-card__grid">
      <label class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.amount || 'Monto a pagar' }}</span>
        <AppMoneyInput
          v-model="receivedAmountModel"
          output-mode="number"
          :decimal-places="2"
          :minimum-value="0"
          :currency="currency"
        />
      </label>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.date || 'Fecha' }}</span>
        <VueDatePicker
          :model-value="info.date"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.date || 'Selecciona fecha'"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          model-type="yyyy-MM-dd"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="generalText.common.today"
          @update:model-value="$emit('info-change', { key: 'date', value: $event || '' })"
        />
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.paymentForm || 'Forma de pago' }}</span>
        <multiselect
          v-model="paymentFormModel"
          :options="paymentFormOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="false"
          class="app-modal-select"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.paymentForm || 'Selecciona forma de pago'"
        >
          <template #noResult>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.paymentAccount || 'Cuenta receptora' }}</span>
        <multiselect
          v-model="paymentAccountModel"
          :options="paymentAccountOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.paymentAccount || 'Selecciona cuenta receptora'"
        >
          <template #noResult>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.paymentInfoCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <label class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.paymentInfoCard?.fields?.operationNumber || 'No. operación / referencia' }}</span>
        <input
          v-model.trim="operationNumberModel"
          type="text"
          class="form-control"
          maxlength="80"
          autocomplete="off"
          :placeholder="text?.create?.ui?.paymentInfoCard?.placeholders?.operationNumber || 'Captura número de operación'"
        />
      </label>

    </div>

    <div v-if="showBankFields" class="payment-receipt-payment-info-card__banks">
      <PayModalSectionBanks
        :text="paymentsText"
        :expanded="bankDetails.expanded"
        :beneficiary-bank-rfc="bankDetails.beneficiaryBankRfc"
        :beneficiary-account-number="bankDetails.beneficiaryAccountNumber"
        :ordering-bank="bankDetails.orderingBank"
        :ordering-bank-rfc="bankDetails.orderingBankRfc"
        :ordering-bank-account="bankDetails.orderingBankAccount"
        @update:expanded="updateBankField('expanded', $event)"
        @update:beneficiaryBankRfc="updateBankField('beneficiaryBankRfc', $event)"
        @update:beneficiaryAccountNumber="updateBankField('beneficiaryAccountNumber', $event)"
        @update:orderingBank="updateBankField('orderingBank', $event)"
        @update:orderingBankRfc="updateBankField('orderingBankRfc', $event)"
        @update:orderingBankAccount="updateBankField('orderingBankAccount', $event)"
      />
    </div>
  </article>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import PayModalSectionBanks from '@/components/pay/modal/sections/PayModalSectionBanks.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: generalText } = useDomainText('general');

const BANK_DETAILS_ENABLED_PAYMENT_FORMS = new Set([
  '02',
  '03',
  '04',
  '05',
  '06',
  '28',
  '29',
  '99',
]);

export default {
  name: 'PaymentReceiptCreatePaymentInfoCard',
  components: {
    AppMoneyInput,
    PayModalSectionBanks,
    VueDatePicker,
  },
  props: {
    text: {
      type: Object,
      default: () => ({}),
    },
    paymentsText: {
      type: Object,
      required: true,
    },
    info: {
      type: Object,
      required: true,
    },
    paymentFormOptions: {
      type: Array,
      default: () => [],
    },
    paymentAccountOptions: {
      type: Array,
      default: () => [],
    },
    bankDetails: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    voucherType: {
      type: String,
      default: 'cfdi',
    },
  },
  emits: ['info-change', 'update:bank-details'],
  data() {
    return {
      generalText,
    };
  },
  computed: {
    receivedAmountModel: {
      get() {
        return Number(this.info?.receivedAmount || 0) || 0;
      },
      set(value) {
        this.$emit('info-change', { key: 'receivedAmount', value: Number(value || 0) || 0 });
      },
    },
    paymentFormModel: {
      get() {
        const selectedCode = String(this.info?.paymentForm || '').trim();
        return this.paymentFormOptions.find((option) => String(option?.value || '').trim() === selectedCode)
          || this.paymentFormOptions[0]
          || null;
      },
      set(option) {
        this.$emit('info-change', { key: 'paymentForm', value: String(option?.value || '').trim() });
      },
    },
    paymentAccountModel: {
      get() {
        const selectedId = String(this.info?.paymentAccountId || '').trim();
        return this.paymentAccountOptions.find((option) => String(option?.value || '').trim() === selectedId)
          || this.paymentAccountOptions[0]
          || null;
      },
      set(option) {
        this.$emit('info-change', { key: 'paymentAccountId', value: String(option?.value || '').trim() });
      },
    },
    operationNumberModel: {
      get() {
        return String(this.info?.operationNumber || '').trim();
      },
      set(value) {
        this.$emit('info-change', { key: 'operationNumber', value: String(value || '').trim() });
      },
    },
    showBankFields() {
      const normalizedVoucherType = String(this.voucherType || 'cfdi').trim().toLowerCase();
      if (normalizedVoucherType === 'simple_note') {
        return false;
      }

      const paymentFormCode = String(this.info?.paymentForm || '').trim();
      if (paymentFormCode === '') {
        return false;
      }

      return BANK_DETAILS_ENABLED_PAYMENT_FORMS.has(paymentFormCode);
    },
    paymentFolio() {
      const series = String(this.info?.series || '').trim();
      const folio = String(this.info?.folio || '').trim();
      const value = `${series}${folio}`.trim();
      return value !== '' ? value : '-';
    },
  },
  methods: {
    updateBankField(key, value) {
      const next = {
        ...(this.bankDetails || {}),
        [key]: value,
      };

      this.$emit('update:bank-details', next);
    },
  },
};
</script>

<style scoped>
.payment-receipt-payment-info-card {
  display: grid;
  gap: 8px;
  align-content: start;
  padding-top: 10px;
}

.payment-receipt-payment-info-card .invoice-create-card__head {
  align-items: flex-start;
  margin-bottom: 6px;
}

.payment-receipt-payment-info-card__head-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.payment-receipt-payment-info-card__banks-toggle {
  border: 0;
  background: transparent;
  color: #334155;
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
}

.payment-receipt-payment-info-card__grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.payment-receipt-payment-info-card__folio-box {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px 10px;
}

.payment-receipt-payment-info-card__folio-box strong {
  color: #0f172a;
  font-size: 13px;
}

.payment-receipt-payment-info-card__banks {
  padding-top: 2px;
}

.payment-receipt-payment-info-card__banks :deep(.app-modal__section.payment-plan-pay-modal__section--banks-compact) {
  padding: 0;
}

.payment-receipt-payment-info-card__banks :deep(.payment-plan-pay-modal__bank-extra) {
  margin-top: 0;
  padding-top: 0;
  border-top: 0;
}

.payment-receipt-payment-info-card__banks :deep(.payment-plan-pay-modal__bank-extra-toggle) {
  display: none;
}

@media (max-width: 1200px) {
  .payment-receipt-payment-info-card__grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .payment-receipt-payment-info-card__grid {
    grid-template-columns: 1fr;
  }
}
</style>
